// 004 (FR-001–FR-008; P-PROF-1..4; US2): the parent Profile. Order: details → My children →
// Delivery addresses → Settings. At desktop width the details sit in a side card; on phones
// everything stacks. Data comes from the parent data store, refreshed on focus; offline keeps the
// last data and disables changes.
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback } from "react";
import { View } from "react-native";

import { AUTH_COPY } from "@/auth/copy";
import { useSessionController } from "@/session/hooks";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { StateView } from "@/shell/StateView";
import { ThemeChoice } from "@/shell/ThemeChoice";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { useRoleActions } from "@/shell/useRoleActions";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { AddressesSection } from "./AddressesSection";
import { ChildrenSection } from "./ChildrenSection";
import { PROFILE_COPY } from "./copy";
import {
  Avatar,
  Card,
  OfflineNote,
  ProfileFrame,
  SectionHead,
  useProfileStyles,
} from "./ui";
import { useParentData } from "./useParentData";
import { addressVM, childVM, profileVM } from "./viewModels";

function DetailsCard({ online }: { online: boolean }) {
  const styles = useProfileStyles();
  const router = useRouter();
  const { profile } = useParentData();
  if (!profile.data) return null;
  const vm = profileVM(profile.data);
  return (
    <Card testID="profile-details">
      <SectionHead title={PROFILE_COPY.details} />
      <View style={styles.actions}>
        <Avatar text={vm.initials} />
        <View style={styles.rowText}>
          <Text weight="semiBold">{vm.name}</Text>
          <Text tone="muted">{vm.email}</Text>
          <Text tone="muted">{vm.mobileDisplay ?? PROFILE_COPY.noMobile}</Text>
        </View>
      </View>
      <PressableTarget
        disabled={!online}
        onPress={() => router.push("/profile/edit")}
      >
        {PROFILE_COPY.editDetails}
      </PressableTarget>
    </Card>
  );
}

function SettingsSection() {
  const { signOut } = useSessionController();
  const router = useRouter();
  const roleActions = useRoleActions();
  const logOut = () => {
    // Same event: the session change and the navigation render together (002).
    void signOut("USER");
    router.replace("/welcome");
  };
  return (
    <Card testID="profile-settings">
      <SectionHead title={PROFILE_COPY.settings} />
      <ThemeChoice />
      {roleActions.map((action) => (
        <PressableTarget key={action.id} onPress={action.onSelect}>
          {action.label}
        </PressableTarget>
      ))}
      <PressableTarget onPress={logOut}>
        {AUTH_COPY.actions.logOut}
      </PressableTarget>
    </Card>
  );
}

export function ProfileScreen() {
  const styles = useProfileStyles();
  const chrome = useChromeKind();
  const { online } = useNetworkStatus();
  const { profile, students, addresses, actions } = useParentData();

  useFocusEffect(
    useCallback(() => {
      void actions.refresh();
    }, [actions]),
  );

  const hasData =
    profile.data !== null && students.data !== null && addresses.data !== null;
  const firstError = profile.error ?? students.error ?? addresses.error;

  let content;
  if (!hasData && firstError && online) {
    content = (
      <StateView
        status="error"
        error={firstError}
        onRetry={() => void actions.refresh()}
      />
    );
  } else if (!hasData && !online && firstError) {
    content = (
      <StateView status="offline" onRetry={() => void actions.refresh()} />
    );
  } else if (!hasData) {
    content = <StateView status="loading" />;
  }
  if (!hasData) {
    // Settings (theme, role actions, Log out) stay usable while the data loads or fails.
    content = (
      <>
        {content}
        <SettingsSection />
      </>
    );
  } else {
    const children = (students.data ?? []).map(childVM);
    const addressList = (addresses.data ?? []).map(addressVM);
    const sections = (
      <>
        <ChildrenSection items={children} online={online} />
        <AddressesSection items={addressList} online={online} />
        {chrome === "mobile" ? <SettingsSection /> : null}
      </>
    );
    content =
      chrome === "desktop" ? (
        <View style={styles.split}>
          <View style={styles.side}>
            <DetailsCard online={online} />
            <SettingsSection />
          </View>
          <View style={styles.main}>{sections}</View>
        </View>
      ) : (
        <>
          <DetailsCard online={online} />
          {sections}
        </>
      );
  }

  return (
    <ProfileFrame testID="profile-screen">
      <Text
        variant="heading"
        size="x2l"
        accessibilityRole="header"
        aria-level={1}
      >
        {PROFILE_COPY.title}
      </Text>
      <OfflineNote online={online} />
      {content}
    </ProfileFrame>
  );
}
